import { useState, useEffect, useCallback, useMemo } from 'react';
import { Chip, Link } from '@mui/material';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { SttTable, SttTopControls } from '../../../sporttia/all';
import { useCrud, usePageHeader } from '../../../lib/hooks';
import BookingDialog from '../../../components/dialogs/bookingDialog/BookingDialog';
import Group from '../groups/Group';
import Event from '../events/Event';
import Activity from '../activities/Activity';
import Bono from '../bonos/Bono';
import constants from '../../../config/constants';

type Props = {
    id: string | undefined;
};

type DeviceAuth = {
    dateini: string;
    dateend: string;
    timeini: string;
    timeend: string;
    weekdays: string;
    type: 'BK' | 'EV' | 'AC' | 'GP' | 'BN';
    booking?: { id: string; name: string };
    event?: { id: string; name: string };
    activity?: { id: string; name: string };
    group?: { id: string; name: string };
    bono?: { id: string; name: string };
};

export default function DeviceAuths({ id }: Props): React.ReactElement {
    usePageHeader('Authorizations');

    const { t } = useTranslation();

    const [deviceAuths, setDeviceAuths, , Get] = useCrud<{
        data: DeviceAuth[];
        total: number;
    }>();
    const [dateFilter, setDateFilter] = useState<string>(
        format(new Date(), 'yyyy-MM-dd'),
    );
    const [bookingId, setBookingId] = useState<string | null>(null);
    const [eventId, setEventId] = useState<string | null>(null);
    const [activityId, setActivityId] = useState<string | null>(null);
    const [groupId, setGroupId] = useState<string | null>(null);
    const [bonoId, setBonoId] = useState<string | null>(null);

    const loadDeviceAuths = useCallback(
        (params = { rows: 20 }): void => {
            if (!id) return; // Guard against undefined id
            Get(
                `${constants.boundedContextPrefixes.MANAGER}/devices/${id}/auths`,
                {
                    ...params,
                    allDay: true,
                    date: dateFilter,
                },
            ).then((response) =>
                setDeviceAuths(
                    response as { data: DeviceAuth[]; total: number },
                ),
            );
        },
        [dateFilter, id],
    );

    useEffect(() => {
        if (id) {
            loadDeviceAuths();
        }
    }, [loadDeviceAuths, id]);

    const linkStyle = {
        color: 'var(--brand-text)',
        textDecoration: 'none',
        cursor: 'pointer',
        '&:hover': { textDecoration: 'underline' },
    };

    const columns = useMemo(
        () => [
            {
                title: t('Date'),
                type: 'period' as const,
                value: (row: DeviceAuth) => ({
                    ini: row.dateini,
                    end: row.dateend,
                }),
            },
            {
                title: t('Time'),
                type: 'text' as const,
                value: (row: DeviceAuth) =>
                    `${row.timeini.slice(0, -3)} - ${row.timeend.slice(0, -3)}`,
            },
            {
                title: t('Weekdays'),
                type: 'weekdays' as const,
                field: 'weekdays',
            },
            {
                title: t('Type'),
                value: (row: DeviceAuth) => {
                    switch (row.type) {
                        case 'BK':
                            return <Chip size="small" label={t('Booking')} />;
                        case 'EV':
                            return <Chip size="small" label={t('Event')} />;
                        case 'AC':
                            return <Chip size="small" label={t('Activity')} />;
                        case 'GP':
                            return <Chip size="small" label={t('Group')} />;
                        case 'BN':
                            return <Chip size="small" label={t('Bono')} />;
                        default:
                            return null;
                    }
                },
            },
            {
                title: t('Name'),
                align: 'left' as const,
                value: (row: DeviceAuth) => {
                    switch (row.type) {
                        case 'BK':
                            return (
                                <Link
                                    sx={linkStyle}
                                    onClick={() =>
                                        setBookingId(row?.booking?.id || null)
                                    }
                                >
                                    {row?.booking?.name}
                                </Link>
                            );
                        case 'EV':
                            return (
                                <Link
                                    sx={linkStyle}
                                    onClick={() =>
                                        setEventId(row?.event?.id || null)
                                    }
                                >
                                    {row?.event?.name}
                                </Link>
                            );
                        case 'AC':
                            return (
                                <Link
                                    sx={linkStyle}
                                    onClick={() =>
                                        setActivityId(row?.activity?.id || null)
                                    }
                                >
                                    {row?.activity?.name}
                                </Link>
                            );
                        case 'GP':
                            return (
                                <Link
                                    sx={linkStyle}
                                    onClick={() =>
                                        setGroupId(row?.group?.id || null)
                                    }
                                >
                                    {row?.group?.name}
                                </Link>
                            );
                        case 'BN':
                            return (
                                <Link
                                    sx={linkStyle}
                                    onClick={() =>
                                        setBonoId(row?.bono?.id || null)
                                    }
                                >
                                    {row?.bono?.name}
                                </Link>
                            );
                        default:
                            return null;
                    }
                },
            },
        ],
        [t],
    );

    return (
        <>
            <SttTopControls
                p={2}
                fields={[
                    {
                        xs: 12,
                        sm: 12,
                        md: 10,
                        lg: 8,
                        caption: t('Date'),
                        type: 'date',
                        name: 'date',
                        value: dateFilter,
                        onChange: ({ value }: { value: unknown }) =>
                            setDateFilter(
                                value
                                    ? format(
                                          new Date(value as string),
                                          'yyyy-MM-dd',
                                      )
                                    : '',
                            ),
                    },
                ]}
            />
            <SttTable
                autoload={false}
                onFetch={loadDeviceAuths}
                data={deviceAuths}
                columns={columns}
            />

            {bookingId !== null && (
                <BookingDialog
                    idBooking={Number(bookingId)}
                    onUpdate={() => loadDeviceAuths()}
                    onClose={() => setBookingId(null)}
                />
            )}

            {eventId !== null && (
                <Event
                    id={Number(eventId)}
                    trash
                    onChange={() => loadDeviceAuths()}
                    onClose={() => {
                        setEventId(null);
                    }}
                    disableUrlPath
                />
            )}

            <Activity
                id={activityId ? Number(activityId) : null}
                trash
                onChange={() => loadDeviceAuths()}
                onClose={() => {
                    setActivityId(null);
                }}
                disableUrlPath
            />

            {groupId && (
                <Group
                    id={Number(groupId)}
                    trash
                    onChange={() => loadDeviceAuths()}
                    onClose={() => {
                        setGroupId(null);
                    }}
                    disableUrlPath
                />
            )}

            <Bono
                id={bonoId ? Number(bonoId) : null}
                trash={false}
                // Apply changes locally to avoid subsequent petitions (once change is applied, remove items marked with trash)
                onClose={() => {
                    setBonoId(null);
                }}
                onChange={() => {
                    loadDeviceAuths();
                }}
                disableUrlPath
            />
        </>
    );
}
